import type { LiftStatusValue } from "./types";
import type { AreaSport } from "../graph-types";
import { operatorEvidenceFresh, type OperatorEvidenceRow } from "../operations/types";

export const LIFT_STATUS_FRESH_MS = 90 * 60 * 1000;

export type PlannerLiftStatusValue = LiftStatusValue | "unknown";

export type LiftStatusRow = OperatorEvidenceRow & {
  lift_id: string;
  status: PlannerLiftStatusValue;
  scraped_at: string;
  open_time?: string | null;
  close_time?: string | null;
};

export function liftStatusFreshCutoffIso(now: Date): string {
  return new Date(now.getTime() - LIFT_STATUS_FRESH_MS).toISOString();
}

export function latestLiftStatusById(
  rows: readonly LiftStatusRow[],
  liftIds: readonly string[],
  now: Date,
  sport: AreaSport = "ski",
): Map<string, PlannerLiftStatusValue> {
  return new Map([...latestLiftEvidenceById(rows, liftIds, now, sport)].map(([id, row]) => [id, row.status]));
}

export function latestLiftEvidenceById(
  rows: readonly LiftStatusRow[], liftIds: readonly string[], now: Date, sport: AreaSport,
): Map<string, LiftStatusRow> {
  const allowedLiftIds = new Set(liftIds);
  const latest = new Map<string, LiftStatusRow>();

  for (const row of rows) {
    if (!allowedLiftIds.has(row.lift_id)) continue;
    if (!operatorEvidenceFresh(row, sport, now, LIFT_STATUS_FRESH_MS)) continue;
    const existing = latest.get(row.lift_id);
    if (!existing || Date.parse(row.scraped_at) > Date.parse(existing.scraped_at)) {
      latest.set(row.lift_id, row);
    } else if (row.scraped_at === existing.scraped_at && row.status !== existing.status) {
      // Contradictory simultaneous reports must never resolve to an open lift.
      latest.set(row.lift_id, { ...existing, status: "hold" });
    }
  }

  return latest;
}
